/**
 * Board filter <-> URL search params (`?q=&label=..&status=..&deadline=..&as=..`). A category may
 * repeat its parameter (alternatives, OR); unknown and duplicate values are dropped.
 */
import {
  ASSIGNMENT_FILTERS,
  type CardFilter,
  DEADLINE_FILTERS,
  type DeadlineFilter,
  normalizeSelection,
  STATUS_FILTERS,
  type StatusFilter,
} from '@/entities/board';

export function filterFromParams(params: URLSearchParams): CardFilter {
  const status = normalizeSelection(params.getAll('status'), STATUS_FILTERS);
  return {
    q: params.get('q') ?? '',
    labelIds: [...new Set(params.getAll('label'))],
    labelMatch: params.get('match') === 'all' ? 'all' : 'any',
    // Both statuses restrict nothing: the same as "All".
    status: status.length === STATUS_FILTERS.length ? [] : status,
    deadline: normalizeSelection(params.getAll('deadline'), DEADLINE_FILTERS),
    assignment: normalizeSelection(params.getAll('as'), ASSIGNMENT_FILTERS),
  };
}

/** Canonical serialization: option order is fixed, so equal filters give equal URLs. */
export function filterToParams(filter: CardFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.q.trim()) params.set('q', filter.q);
  for (const id of new Set(filter.labelIds)) params.append('label', id);
  if (filter.labelIds.length > 1 && filter.labelMatch === 'all') params.set('match', 'all');
  for (const value of normalizeSelection(filter.status, STATUS_FILTERS)) {
    params.append('status', value satisfies StatusFilter);
  }
  for (const value of normalizeSelection(filter.deadline, DEADLINE_FILTERS)) {
    params.append('deadline', value satisfies DeadlineFilter);
  }
  for (const value of normalizeSelection(filter.assignment, ASSIGNMENT_FILTERS)) {
    params.append('as', value);
  }
  return params;
}
